import { useSyncExternalStore } from 'react';
import { ACCOUNT_HEADER } from '../../shared/api.ts';
import { MOCK_HEADERS, SCORER_MOCKS, type ScorerMock } from '../../shared/mocks.ts';

/**
 * Admin mocks, for this browser tab only (sessionStorage), and only while the server says this is an admin on their
 * test account and admin mode is on (App calls setMocksAllowed). Sign-out clears them.
 *   replay  a recorded Live session instead of Gemini (served by /api/admin/fixtures)
 *   audio   a recorded clip answers every speaking task instead of the mic
 *   scorer  the recorded scores, or "not scored" for a reason, instead of the scoring fleet
 *   review  the content review fails
 * The server honours the scorer and review mocks on an admin's test account only (ADR-0014).
 */
export type Mocks = { replay: string | null; audio: string | null; scorer: ScorerMock | null; review: 'fail' | null };

const KEY = 'fuse.mocks';
let allowed = false;
const NONE: Mocks = { replay: null, audio: null, scorer: null, review: null };
const listeners = new Set<() => void>();

const name = (v: unknown) => (typeof v === 'string' && /^[a-z0-9-]+$/.test(v) ? v : null);

function load(): Mocks {
  try {
    const raw = JSON.parse(sessionStorage.getItem(KEY) ?? '{}') as Record<string, unknown>;
    return {
      replay: name(raw.replay),
      audio: name(raw.audio),
      scorer: SCORER_MOCKS.includes(raw.scorer as ScorerMock) ? (raw.scorer as ScorerMock) : null,
      review: raw.review === 'fail' ? 'fail' : null,
    };
  } catch {
    return NONE;
  }
}

let current = load();

export function setMocks(next: Partial<Mocks>) {
  current = { ...current, ...next };
  try {
    sessionStorage.setItem(KEY, JSON.stringify(current));
  } catch {
    // Blocked storage: the mocks last until reload.
  }
  for (const l of listeners) l();
}

const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};

export const anyMock = (m: Mocks) => Object.values(m).some(Boolean);

/** The stored mocks, for the admin panel. */
export function useMocks(): Mocks {
  return useSyncExternalStore(subscribe, () => current);
}

/**
 * App sets this from /api/me (an admin, on their test account) and the admin-mode switch; a browser flag alone never
 * turns mocks on (ADR-0014).
 */
export function setMocksAllowed(on: boolean) {
  allowed = on;
}

export function clearMocks() {
  setMocks(NONE);
}

/** The mocks in force right now: none unless this is an admin on their test account with admin mode on. */
export function activeMocks(): Mocks | null {
  return allowed && anyMock(current) ? current : null;
}

/** Request headers for the server-side mocks on an answer (ignored anywhere but an admin's test account). */
export function mockHeaders(): Record<string, string> {
  const m = activeMocks();
  return {
    ...(m?.scorer ? { [MOCK_HEADERS.scorer]: m.scorer } : {}),
    ...(m?.review ? { [MOCK_HEADERS.review]: m.review } : {}),
  };
}

let shown: 'test' | 'own' | null = null;
/** App sets this for an admin: the account this tab shows (ADR-0014). Null for everyone else. */
export function setAccountShown(account: 'test' | 'own' | null) {
  shown = account;
}
/**
 * Sent with an admin's call start, so the server refuses a call from a tab still showing the account the browser left
 * (switched, or signed out and in, in another tab, when this tab missed the reload).
 */
export function accountHeaders(): Record<string, string> {
  return shown ? { [ACCOUNT_HEADER]: shown } : {};
}
